import React, { useContext, useEffect, useMemo, useState } from 'react';
import PasswordForm from './common/PasswordForm';
import CardBody from 'components/Card/CardBody';
import { translate } from 'components/Translation';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { finalize } from 'rxjs/operators';
import { useDispatch } from 'react-redux';
import { AuthorizationService } from 'services/authorization/authorization';
import { setToast } from 'redux/modules/toast/actions';
import styles from 'assets/jss/material-dashboard-pro-react/views/setPageStyle';
import { makeStyles } from '@material-ui/core';
import { BodyM, BodyMBold, DisplayS } from 'components/Typography/Typography';
import authContext from 'components/Auth/authContext';
import { useLocation } from 'react-router-dom';
import ErrorMapper from 'utils/api/errors/errorMapper';
import OtpDialog from './common/OtpDialog';
import { ERROR_VALIDATING_TOKEN } from 'utils/api/errors/errorCodes';

// @ts-ignore
const useStyles = makeStyles(styles);

interface FormData {
	password: string;
	repeat_password: string;
}

const SetPage = () => {
	const [token, setToken] = useState<string>('');
	const [loading, setLoading] = useState({ token: false, reset: false, signin: false });
	const [invalidToken, setInvalidToken] = useState(false);
	const location = useLocation();
	const [formData, setFormData] = useState<FormData>({
		password: '',
		repeat_password: '',
	});
	const [isWrongOtp, setIsWrongOtp] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState('');
	const [otp, setOtp] = useState<string>('');
	const [isOpen, setIsOpen] = useState(false);
	const [isOtpLoading, setIsOtpLoading] = useState(false);
	const [loginSuccess, setLoginSuccess] = useState(false);

	const getQuerystring = (key: string) => {
		const query = location.search.substring(1);
		const vars = query.split('&');
		for (let i = 0; i < vars.length; i++) {
			const pair = vars[i].split('=');
			if (pair[0] === key) {
				return pair[1];
			}
		}
	};

	const emailParam = getQuerystring('email');

	const dispatch = useDispatch();
	const AuthContext = useContext(authContext);

	const onChange = (name: keyof FormData, value: string) => setFormData((data) => ({ ...data, [name]: value }));

	const email = useMemo(() => String(emailParam), [emailParam]);

	useEffect(() => {
		const _token = getQuerystring('token');
		if (_token && !token) setToken(_token);

		return () => {
			setToken('');
		};
	}, []);

	useEffect(() => {
		if (Object.values(loading).every((value) => !value)) {
			dispatch(hideLoadingIndicator());
		} else {
			dispatch(showLoadingIndicator());
		}
	}, [loading, dispatch]);

	useEffect(() => {
		if (token.length > 0) {
			setInvalidToken(false);
			setLoading((loading) => ({ ...loading, token: true }));
			AuthorizationService.validateResetPasswordEmail(email, String(token))
				.pipe(finalize(() => setLoading((loading) => ({ ...loading, token: false }))))
				.subscribe(({ response }: any) => {
					if (response.arguments.returnValue !== 'true') {
						setInvalidToken(true);
					}
				});
		} else {
			setInvalidToken(true);
		}
	}, [email, token]);

	const requestForOtp = () => {
		dispatch(showLoadingIndicator());
		setIsOpen(false);
		AuthorizationService.requestResetPasswordOtp(email)
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
				})
			)
			.subscribe(
				() => {
					setServerErrorMessage('');
					setToast({
						error: false,
						label: translate('otp.token.sent'),
					});
					setIsOpen(true);
				},
				(error) => {
					setServerErrorMessage(ErrorMapper.mapToMessage(error.response.code));
				}
			);
	};

	const onSubmit = () => {
		setLoading((loading) => ({ ...loading, reset: true }));
		setIsOtpLoading(true);
		AuthorizationService.resetPassword({
			email,
			password: formData.password,
			password2: formData.repeat_password,
			otp,
		})
			.pipe(finalize(() => setLoading((loading) => ({ ...loading, reset: false }))))
			.subscribe(
				({ response }: any) => {
					if (response.arguments.returnValue === 'true') {
						setLoginSuccess(true);
						setIsOtpLoading(false);
						setIsOpen(false);
						setToken('');
						dispatch(
							setToast({
								error: false,
								label: translate('set.password.your.password.changed'),
								actionType: translate('set.password.new.password'),
							})
						);
						AuthContext.handleAuthentication({ user: String(emailParam), secret: formData.password });
					}
				},
				(error) => {
					setIsOtpLoading(false);
					if (error?.response?.code === ERROR_VALIDATING_TOKEN) {
						setIsWrongOtp(true);
						return;
					}
				}
			);
	};

	const classes = useStyles();

	if (loading.token) {
		return <div className={classes.empty} />;
	} else if (invalidToken && !loginSuccess) {
		return (
			<CardBody>
				<BodyM>{translate('set.password.sorry')}</BodyM>
				<div className={classes.contact}>
					<BodyM>{translate('set.password.contact')}</BodyM>
				</div>
			</CardBody>
		);
	} else {
		return (
			<>
				<CardBody>
					<BodyMBold>{translate('setPassword.subheader', email)}</BodyMBold>
					<DisplayS color="brand">{translate('setPassword.header')}</DisplayS>
					<PasswordForm formData={formData} onChange={onChange} onSubmit={requestForOtp} />
				</CardBody>
				<OtpDialog
					key={isOpen ? translate('badge.active') : translate('badge.inactive')}
					otp={otp}
					setOtp={setOtp}
					isOpen={isOpen}
					isWrongOtp={isWrongOtp}
					onSubmit={onSubmit}
					onClose={() => {
						setIsOpen(false);
						setOtp('');
						setIsWrongOtp(false);
					}}
					heading={translate('forgot.form.reset')}
					description={translate('number.settings.password.sent')}
					setIsWrongOtp={setIsWrongOtp}
					loading={isOtpLoading}
					sendOtp={requestForOtp}
				/>
			</>
		);
	}
};

export default SetPage;
